import type { Role, User } from "@types";

/**
 * Roles that may configure phone system settings — the set the `/phone/*` admin
 * pages gate on. Single source so the route gate (`router/routeConfig.ts`) and
 * the FIFO-config helper (`libs/Phone/queues/types.ts` → `canConfigureFifoQueues`)
 * can't drift; Greptile flagged the earlier duplicated literals. Adding a `Role`
 * to the union forces a decision here rather than a silent gap. Dale stays the
 * authority for the FIFO write itself (it 403s anyone it rejects).
 */
export const PHONE_SETTINGS_ROLES: ReadonlySet<Role> = new Set<Role>([
  "super",
  "admin",
  "owner",
]);

/**
 * Coach permission strings — the FE side of the RBAC wire contract.
 *
 * These are EXACT server-authored strings (dale `Permission` enum, #4684); never
 * rename or reformat them, they ride in JWTs/caches and on `user.permissions`.
 * Mirror, don't invent: if the backend adds a coach.* permission, add it here.
 */
export const COACH_PERMISSIONS = {
  conversationRead: "coach.conversation.read",
  dashboardRead: "coach.dashboard.read",
  agentPerformanceRead: "coach.dashboard.agent_performance.read",
  configRead: "coach.config.read",
  configWrite: "coach.config.write",
  agentManage: "coach.agent.manage",
  export: "coach.export",
} as const;

/** Every coach permission a fully-privileged role holds. */
const ALL_COACH_PERMISSIONS: readonly string[] =
  Object.values(COACH_PERMISSIONS);

/**
 * Insights permission strings — the FE side of the RBAC wire contract for the
 * super-admin Insights page. EXACT server-authored strings (dale `Permission`
 * enum); mirror, don't invent.
 *
 * `financialsView` gates the Financials tab (company cash/burn/COGS/payroll —
 * the most sensitive data in the app). It is DELIBERATELY excluded from the
 * role fallback below: unlike coach permissions, being `super` does NOT confer
 * it. Only an explicit server grant (`user.permissions` including this string)
 * unlocks it, so the gate fails closed for every super admin the CEO hasn't
 * granted. `financialsManageAccess` gates the CEO's grant/revoke panel.
 */
export const INSIGHTS_PERMISSIONS = {
  financialsView: "insights.financials.view",
  financialsManageAccess: "insights.financials.manage_access",
} as const;

/**
 * Field Sales permission strings — the FE side of the RBAC wire contract for
 * the Field Sales / canvassing module (SD-10989, FIELD-SALES-BUILD-SPEC.md §2.1).
 *
 * BINDING for dale: these exact strings must land in the dale `Permission`
 * enum when the `rep` role ships server-side. Mirror, don't invent.
 */
export const FIELD_SALES_PERMISSIONS = {
  territoryRead: "field_sales.territory.read",
  territoryManage: "field_sales.territory.manage",
  prospectWrite: "field_sales.prospect.write",
  reviewRead: "field_sales.review.read",
  statsRead: "field_sales.stats.read",
  gamificationManage: "field_sales.gamification.manage",
} as const;

/** Every field-sales permission a manager-tier role holds. */
const ALL_FIELD_SALES_PERMISSIONS: readonly string[] = Object.values(
  FIELD_SALES_PERMISSIONS,
);

/** The restricted `rep` grant: read own territories/stats, write own prospects. */
const REP_PERMISSIONS: readonly string[] = [
  FIELD_SALES_PERMISSIONS.territoryRead,
  FIELD_SALES_PERMISSIONS.prospectWrite,
  FIELD_SALES_PERMISSIONS.statsRead,
];

/** Native Dispatch permission strings; mirrors dale's Permission enum. */
export const DISPATCH_PERMISSIONS = {
  boardRead: "dispatch.board.read",
  boardManage: "dispatch.board.manage",
  rosterManage: "dispatch.roster.manage",
  technicianSelf: "dispatch.technician.self",
  customerRead: "dispatch.customer.read",
  customerManage: "dispatch.customer.manage",
  estimateManage: "dispatch.estimate.manage",
  invoiceManage: "dispatch.invoice.manage",
  inventoryManage: "dispatch.inventory.manage",
  purchaseApprove: "dispatch.purchase.approve",
  dataExport: "dispatch.data.export",
} as const;

const ALL_DISPATCH_PERMISSIONS: readonly string[] =
  Object.values(DISPATCH_PERMISSIONS);

const DISPATCHER_RESTRICTED_PERMISSIONS = new Set<string>([
  DISPATCH_PERMISSIONS.technicianSelf,
  DISPATCH_PERMISSIONS.purchaseApprove,
  DISPATCH_PERMISSIONS.dataExport,
]);

const DISPATCHER_PERMISSIONS: readonly string[] =
  ALL_DISPATCH_PERMISSIONS.filter(
    (permission) => !DISPATCHER_RESTRICTED_PERMISSIONS.has(permission),
  );

/**
 * TRANSITIONAL CEO bootstrap for Financials access.
 *
 * The `insights.financials.*` permissions are fail-closed — nobody holds them
 * until dale issues the grant in the token. Until that ships, the CEO (the
 * grantor) would be locked out of their own Financials tab and the access
 * manager. These emails always resolve BOTH financials permissions so the
 * feature is usable day one.
 *
 * FE permission checks are UX-only (dale is the data security boundary), so an
 * email bootstrap here changes what the CEO SEES, not what the API returns.
 * DELETE this the moment dale seeds the CEO's token with the two grants — from
 * then on `user.permissions` is the sole authority.
 */
const FINANCE_ACCESS_BOOTSTRAP_EMAILS: ReadonlySet<string> = new Set([
  "aaron@choosesameday.com",
]);

/**
 * Role → permission fallback, mirroring the dale `ROLE_PERMISSIONS` matrix.
 *
 * TRANSITIONAL: this only exists so sessions with OLD tokens (no
 * `user.permissions` field) keep working until they re-login. Once tokens have
 * rolled over, `permissionsForUser` can drop the fallback and read
 * `user.permissions` unconditionally, and this map can be deleted.
 *
 * `super` is staff (all permissions); owner/admin get the full coach set plus
 * the full field-sales set; member gets the coach set only (members don't see
 * Field Sales in v1 — plan §3.6); `coach_csr` is the restricted pilot role (its
 * two row-scoped reads only); `rep` is the restricted canvasser role
 * (territory/stat reads plus prospect writes only).
 */
const ROLE_PERMISSIONS = {
  partner: [],
  super: [
    ...ALL_COACH_PERMISSIONS,
    ...ALL_FIELD_SALES_PERMISSIONS,
    ...ALL_DISPATCH_PERMISSIONS,
  ],
  owner: [
    ...ALL_COACH_PERMISSIONS,
    ...ALL_FIELD_SALES_PERMISSIONS,
    ...ALL_DISPATCH_PERMISSIONS,
  ],
  admin: [
    ...ALL_COACH_PERMISSIONS,
    ...ALL_FIELD_SALES_PERMISSIONS,
    ...ALL_DISPATCH_PERMISSIONS,
  ],
  member: ALL_COACH_PERMISSIONS,
  coach_csr: [
    COACH_PERMISSIONS.conversationRead,
    COACH_PERMISSIONS.agentPerformanceRead,
  ],
  rep: REP_PERMISSIONS,
  dispatcher: DISPATCHER_PERMISSIONS,
  technician: [DISPATCH_PERMISSIONS.technicianSelf],
} satisfies Record<Role, readonly string[]>;

/**
 * Resolve the effective permission set for a user.
 *
 * Prefers the server-authored `user.permissions` (the authority). Falls back to
 * the role-derived set only when that field is absent (old token) — see
 * `ROLE_PERMISSIONS`. An undefined user (logged out) has no permissions.
 *
 * Pure + framework-free so it's directly unit-testable; `usePermissions` is a
 * thin React/Redux wrapper over it.
 */
export const permissionsForUser = (user?: User | null): Set<string> => {
  if (!user) return new Set();
  const base =
    user.permissions !== undefined
      ? new Set(user.permissions)
      : new Set<string>(ROLE_PERMISSIONS[user.role] ?? []);
  // CEO bootstrap (transitional — see FINANCE_ACCESS_BOOTSTRAP_EMAILS). Applied
  // after the base set so it holds whether or not the token carries a
  // permissions array.
  if (
    user.email &&
    FINANCE_ACCESS_BOOTSTRAP_EMAILS.has(user.email.toLowerCase())
  ) {
    base.add(INSIGHTS_PERMISSIONS.financialsView);
    base.add(INSIGHTS_PERMISSIONS.financialsManageAccess);
  }
  return base;
};

/**
 * Role → invitable-roles fallback, mirroring the dale `_INVITE_ASSIGNABLE_ROLES`
 * matrix (the ceiling the invite gate enforces).
 *
 * TRANSITIONAL: only used when `user.inviteAssignableRoles` is ABSENT (old token,
 * pre-#4703) so currently-logged-in inviters don't get an empty dropdown until
 * they re-login. Once tokens roll over, `inviteAssignableRolesForUser` can read
 * the served field unconditionally and this map can be deleted.
 *
 * Staff (super) bypasses the ceiling and may assign any role EXCEPT `super`
 * itself. owner/admin gain the low-privilege `coach_csr` grant; member/coach_csr
 * may invite no one.
 */
const ROLE_INVITE_ASSIGNABLE_ROLES = {
  super: [
    "owner",
    "admin",
    "member",
    "coach_csr",
    "rep",
    "dispatcher",
    "technician",
  ],
  owner: ["admin", "member", "coach_csr", "rep", "dispatcher", "technician"],
  admin: ["member", "coach_csr", "rep", "dispatcher", "technician"],
  member: [],
  partner: [],
  coach_csr: [],
  rep: [],
  dispatcher: [],
  technician: [],
} satisfies Partial<Record<Role, readonly string[]>>;

const ROLE_UPDATE_ASSIGNABLE_ROLES = {
  super: [
    "super",
    "owner",
    "admin",
    "member",
    "coach_csr",
    "rep",
    "dispatcher",
    "technician",
  ],
  owner: [
    "owner",
    "admin",
    "member",
    "coach_csr",
    "rep",
    "dispatcher",
    "technician",
  ],
  admin: ["member", "coach_csr", "rep", "dispatcher", "technician"],
  member: [],
  partner: [],
  coach_csr: [],
  rep: [],
  dispatcher: [],
  technician: [],
} satisfies Partial<Record<Role, readonly string[]>>;

const ROLE_UPDATE_MANAGEABLE_ROLES = {
  super: [
    "super",
    "owner",
    "admin",
    "member",
    "coach_csr",
    "rep",
    "dispatcher",
    "technician",
  ],
  owner: [
    "owner",
    "admin",
    "member",
    "coach_csr",
    "rep",
    "dispatcher",
    "technician",
  ],
  admin: ["member", "coach_csr", "rep", "dispatcher", "technician"],
  member: [],
  partner: [],
  coach_csr: [],
  rep: [],
  dispatcher: [],
  technician: [],
} satisfies Partial<Record<Role, readonly string[]>>;

/**
 * Resolve the roles a user may assign via invite.
 *
 * Prefers the server-authored `user.inviteAssignableRoles` (the authority, incl.
 * an authoritative empty `[]`). Falls back to the role-derived set only when the
 * field is absent (old token) — see `ROLE_INVITE_ASSIGNABLE_ROLES`. An undefined
 * user (logged out) may invite no one. Pure + framework-free for unit testing.
 */
export const inviteAssignableRolesForUser = (user?: User | null): string[] => {
  if (!user) return [];
  if (user.inviteAssignableRoles !== undefined)
    return [...user.inviteAssignableRoles];
  return [...(ROLE_INVITE_ASSIGNABLE_ROLES[user.role] ?? [])];
};

export const roleUpdateAssignableRolesForUser = (
  user?: User | null,
): string[] => {
  if (!user) return [];
  if (user.roleUpdateAssignableRoles !== undefined)
    return [...user.roleUpdateAssignableRoles];
  return [...(ROLE_UPDATE_ASSIGNABLE_ROLES[user.role] ?? [])];
};

export const roleUpdateManageableRolesForUser = (
  user?: User | null,
): string[] => {
  if (!user) return [];
  if (user.roleUpdateManageableRoles !== undefined)
    return [...user.roleUpdateManageableRoles];
  return [...(ROLE_UPDATE_MANAGEABLE_ROLES[user.role] ?? [])];
};

/**
 * The canonical user blob for ALL gating decisions: parsed from localStorage.
 *
 * localStorage is the single source of truth for route/permission gating across
 * the app — the route loader (outside React, can't read Redux), the shell's
 * `userIsAuthorized`, the coach nav, `<Can>`, and `CoachIndex` all read it, so
 * none can disagree. Login writes localStorage + Redux atomically from the same
 * blob, and multi-tab shares localStorage but not Redux, so localStorage is
 * never staler than Redux for gating.
 *
 * Tradeoff: localStorage is NOT reactive — gated UI (e.g. the coach sub-nav)
 * won't live-update without a navigation. That's acceptable because permissions
 * only change on login/logout today. Phase B (/me + CompanyMembership) revisits
 * this with a tab-synced reactive store.
 *
 * Returns undefined when logged out, outside the browser, or on corrupt data.
 */
export const currentUser = (): User | undefined => {
  if (globalThis.window === undefined) return undefined;
  const raw = window.localStorage.getItem("user");
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return undefined;
  }
};

/** Effective permissions of the current (localStorage) user — the gating source. */
export const currentUserPermissions = (): Set<string> =>
  permissionsForUser(currentUser());

/** Roles the current (localStorage) user may assign via invite. */
export const currentUserInviteAssignableRoles = (): string[] =>
  inviteAssignableRolesForUser(currentUser());
